// #tag: NOTIFICATIONS AND CALENDAR API CLIENT
// #purpose: Dispatches API requests for student notification inboxes, read receipts, delivery preferences, exam subscriptions, and calendar agendas.
import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type {
  CalendarEvent,
  Notification,
  NotificationCategoryMeta,
  NotificationPreferences,
  SavedExam,
} from '../types';

export interface NotificationInboxResponse {
  data: Notification[];
  meta: { total: number; unread: number; followedExams: number };
}

export interface NotificationConfigResponse {
  categories: NotificationCategoryMeta[];
  channels: string[];
  channelMatrix: Record<string, Record<string, boolean>>;
  defaultPreferences: NotificationPreferences;
}

export function fetchNotificationConfig(): Promise<NotificationConfigResponse> {
  return apiGet('/notifications/config');
}

export function fetchNotifications(params?: { categoryId?: string; limit?: number }): Promise<NotificationInboxResponse> {
  const sp = new URLSearchParams();
  if (params?.categoryId) sp.set('categoryId', params.categoryId);
  if (params?.limit) sp.set('limit', String(params.limit));
  const qs = sp.toString();
  return apiGet(`/notifications${qs ? `?${qs}` : ''}`);
}

export function markNotificationRead(id: string): Promise<{ ok: boolean }> {
  return apiPost(`/notifications/${id}/read`, {});
}

export function markAllNotificationsRead(): Promise<{ ok: boolean }> {
  return apiPost('/notifications/read-all', {});
}

export function dismissNotification(id: string): Promise<{ ok: boolean }> {
  return apiPost(`/notifications/${id}/dismiss`, {});
}

export function fetchNotificationPreferences(): Promise<{ preferences: NotificationPreferences }> {
  return apiGet('/notifications/preferences');
}

export function updateNotificationPreferences(preferences: Partial<NotificationPreferences>): Promise<{ preferences: NotificationPreferences }> {
  return apiPut('/notifications/preferences', { preferences });
}

export function fetchFollowedExams(): Promise<{ data: Array<Record<string, unknown>> }> {
  return apiGet('/notifications/follows');
}

export function followExam(exam: SavedExam): Promise<SavedExam> {
  return apiPost('/notifications/follows', {
    examId: exam.examId || exam.id,
    examTitle: exam.examTitle || exam.title,
    organization: exam.organization,
    examCategory: exam.examCategory,
  });
}

export function syncFollowedExams(follows: Array<Partial<SavedExam> & { examId?: string; examTitle?: string }>): Promise<{ data: Array<Record<string, unknown>> }> {
  return apiPut('/notifications/follows', {
    follows: follows.map((exam) => ({
      examId: exam.examId || exam.id,
      examTitle: exam.examTitle || exam.title,
      organization: exam.organization,
      examCategory: exam.examCategory,
      followedAt: exam.followedAt || exam.savedAt,
    })),
  });
}

export function unfollowExam(examId: string): Promise<{ ok: boolean }> {
  return apiDelete(`/notifications/follows/${examId}`);
}

export function fetchCalendarEvents(params?: {
  from?: string;
  to?: string;
  limit?: number;
  examIds?: string[];
  upcoming?: boolean;
}): Promise<{ data: CalendarEvent[] }> {
  const sp = new URLSearchParams();
  if (params?.from) sp.set('from', params.from);
  if (params?.to) sp.set('to', params.to);
  if (params?.limit) sp.set('limit', String(params.limit));
  if (params?.upcoming) sp.set('upcoming', 'true');
  if (params?.examIds?.length) sp.set('examIds', params.examIds.join(','));
  const qs = sp.toString();
  return apiGet(`/calendar/events${qs ? `?${qs}` : ''}`);
}
